import React, { ChangeEvent } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import type { SelectProps, SelectItemProps } from '@radix-ui/react-select';
import cx from 'classnames';

type OptionProp = {
    label: string;
    value: string;
    optgroup?: string;
    selected?: boolean;
    disabled?: boolean;
};

// Before normalization, user can pass options as string or OptionProp
type Option = OptionProp | string;
type Options = Array<Option>;

// After normalization, the options will be stored in a Map as group of options
type OptionGroup = OptionProp | OptionProp[];
type OptionsGroup = Map<string, OptionGroup>;

type SelectInputProps = {
    /**
     * options that will be rendered as children, you can also construct your option markup as children
     */
    label?: string;
    options?: Options;
    placeholder?: string;
} & SelectProps &
    React.ComponentPropsWithRef<'select'>;

const normalizeOption = (option: OptionProp | string): OptionProp => {
    return typeof option === 'string' ? { value: option, label: option.charAt(0).toUpperCase() + option.slice(1) } : option;
};

const normalizeOptionGroup = (options: Options): OptionsGroup => {
    const normalizedOptions: OptionsGroup = new Map();
    options.map((option: OptionProp, index: number) => {
        if (option.optgroup !== undefined) {
            if (!normalizedOptions.has(option.optgroup)) {
                normalizedOptions.set(option.optgroup, []);
            }
            (normalizedOptions.get(option.optgroup) as OptionProp[])?.push(normalizeOption(option));
        } else {
            normalizedOptions.set(`${index}`, normalizeOption(option));
        }
    });
    return normalizedOptions;
};

const renderOption = (opt: OptionProp, key: string) => {
    return (
        <SelectPrimitive.Item key={key} value={opt.value} disabled={opt.disabled} className="flos-custom-select-item">
            <SelectPrimitive.ItemText>{opt.label}</SelectPrimitive.ItemText>
        </SelectPrimitive.Item>
    );
};

const renderOptions = (options: OptionProp[], groupkey: string) => {
    const groupedOptionOutput: JSX.Element[] = [];
    Array.prototype.forEach.call(options, (option: OptionProp, index: number) => {
        groupedOptionOutput.push(renderOption(option, `${groupkey}-${index}`));
    });
    return groupedOptionOutput;
};

const renderOptionGroup = (options: OptionsGroup) => {
    const output: JSX.Element[] = [];
    options.forEach((option: OptionGroup, key: string) => {
        // check if key is index liked
        if (!isNaN(parseInt(`${key}`))) {
            output.push(renderOption(option as OptionProp, key));
        } else if (Array.isArray(option)) {
            // else check if it is an array of options
            const groupedOptionOutput = renderOptions(option, key);
            if (groupedOptionOutput.length) {
                output.push(
                    <SelectPrimitive.Group key={key}>
                        <SelectPrimitive.Label>{key}</SelectPrimitive.Label>
                        {groupedOptionOutput}
                    </SelectPrimitive.Group>
                );
            }
        }
    });
    return output;
};

const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(({ children, ...props }: SelectItemProps, forwardedRef) => {
    return (
        <SelectPrimitive.Item {...props} ref={forwardedRef} className="flos-custom-select-item">
            <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        </SelectPrimitive.Item>
    );
});

const SelectInput = React.forwardRef<HTMLButtonElement, SelectInputProps>(
    ({ children, placeholder, options, label, name, id, onValueChange, onChange, ...props }: SelectInputProps, forwardedRef) => {
        const optionsGroupMap: OptionsGroup = React.useMemo(() => (options ? normalizeOptionGroup(options) : new Map()), [options]);
        const localRef = React.useRef(null);
        const ref = (forwardedRef || localRef) as React.RefObject<HTMLButtonElement>;

        const localOnValueChange: SelectInputProps['onValueChange'] = (value) => {
            onChange && onChange({ target: { value, name }, stopPropagation: () => {}, preventDefault: () => {} } as ChangeEvent<HTMLSelectElement>);
            onValueChange && onValueChange(value);
        };

        return (
            <SelectPrimitive.Root {...props} onValueChange={localOnValueChange} name={name}>
                <SelectPrimitive.Trigger
                    ref={ref}
                    id={id}
                    className={cx(
                        'flos-button',
                        'flos-button--secondary',
                        'flos-button--medium',
                        'flos-custom-select-trigger',
                        'flos-input',
                        !label && 'flos-custom-select-trigger--no-label'
                    )}
                >
                    <SelectPrimitive.Value placeholder={placeholder ? placeholder : 'Vælg...'} />
                </SelectPrimitive.Trigger>
                <SelectPrimitive.Portal>
                    <SelectPrimitive.Content position="popper" className="flos-custom-select-outer">
                        <ScrollArea.Root type="auto" className="flos-custom-select-scroll-area">
                            <SelectPrimitive.Viewport asChild>
                                <ScrollArea.Viewport className="flos-custom-select-scroll-area-viewport" style={{ overflowY: undefined }}>
                                    {options ? renderOptionGroup(optionsGroupMap) : children}
                                </ScrollArea.Viewport>
                            </SelectPrimitive.Viewport>
                            <ScrollArea.Scrollbar className="flos-custom-select-scroll-area-scrollbar" orientation="vertical">
                                <ScrollArea.Thumb className="flos-custom-select-scroll-area-thumb" />
                            </ScrollArea.Scrollbar>
                        </ScrollArea.Root>
                    </SelectPrimitive.Content>
                </SelectPrimitive.Portal>
            </SelectPrimitive.Root>
        );
    }
);

export { SelectInput, SelectItem, normalizeOption, normalizeOptionGroup, type SelectInputProps, type Option, type OptionProp, type Options, type OptionGroup, type OptionsGroup };
export default SelectInput;
